import { useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

/**
 * DbHealthBanner — polls /api/admin/db-health every 30s and surfaces any
 * MongoDB Atlas transient incidents in the last hour (failovers, timeouts,
 * slow queries). Collapses to a small green pill when everything's healthy.
 */
export function DbHealthBanner({ API }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const res = await axios.get(`${API}/admin/db-health`, { timeout: 5000 });
        if (!cancelled) setHealth(res.data);
      } catch (e) {
        if (!cancelled) setHealth({ healthy: false, incidents_last_hour: -1, offline: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHealth();
    const timer = setInterval(fetchHealth, 30000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [API]);

  if (loading || !health) return null;

  // Healthy — tiny green pill (non-intrusive)
  if (health.healthy) {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-mono"
        data-testid="db-health-healthy"
        title="Database healthy — no incidents in the last hour"
      >
        <CheckCircle2 className="w-3 h-3" />
        <span>DB OK</span>
      </div>
    );
  }

  // Unhealthy — amber/red banner with popover for details
  const incidents = health.recent_incidents || [];
  const count = health.incidents_last_hour ?? 0;
  const kinds = health.by_kind || {};
  const hasFailover = Object.keys(kinds).some(k =>
    k.includes("ServerSelection") || k.includes("NotPrimary") || k.includes("AutoReconnect"));
  const severityClass = hasFailover
    ? "bg-red-500/10 border-red-500/40 text-red-300"
    : "bg-amber-500/10 border-amber-500/40 text-amber-300";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <motion.button
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.02 }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-mono cursor-pointer ${severityClass}`}
          data-testid="db-health-banner"
          title="Click for details"
        >
          <AlertCircle className="w-3.5 h-3.5" />
          <span>
            {health.offline
              ? "DB status unavailable"
              : `${count} DB ${count === 1 ? "hiccup" : "hiccups"} in last hour`}
          </span>
        </motion.button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[22rem] bg-vault-surface border-vault-border text-vault-text p-0"
        data-testid="db-health-popover"
      >
        <div className="p-4 border-b border-vault-border">
          <p className="font-outfit text-sm font-semibold mb-1">Database Health</p>
          <p className="text-xs text-vault-text-secondary">
            {health.offline
              ? "Couldn't reach the health endpoint. Server may be restarting."
              : `${count} transient MongoDB Atlas incident${count === 1 ? "" : "s"} in the last hour.`}
          </p>
          {Object.keys(kinds).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {Object.entries(kinds).map(([k, v]) => (
                <span key={k} className="text-[10px] font-mono px-2 py-0.5 rounded bg-vault-surface-highlight text-vault-text-secondary">
                  {k}: {v}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {incidents.length === 0 ? (
            <p className="p-4 text-xs text-vault-text-secondary">No detailed events available.</p>
          ) : (
            <ul className="divide-y divide-vault-border">
              {incidents.slice(0, 15).map((i, idx) => (
                <li key={idx} className="p-3 text-xs" data-testid={`db-incident-${idx}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono font-semibold text-vault-gold">{i.kind}</span>
                    <span className="text-[10px] text-vault-text-secondary whitespace-nowrap">
                      {new Date(i.at).toLocaleTimeString()}
                    </span>
                  </div>
                  {i.latency_ms > 0 && (
                    <div className="text-[10px] text-vault-text-secondary mt-0.5">
                      Latency: {i.latency_ms}ms
                    </div>
                  )}
                  <div className="mt-1 text-vault-text-secondary break-words line-clamp-3">
                    {i.message || "(no details)"}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-3 border-t border-vault-border bg-vault-surface-highlight/30 text-[10px] text-vault-text-secondary">
          Check-in retries automatically on these errors. Guests rarely see them.
        </div>
      </PopoverContent>
    </Popover>
  );
}
